import { useState } from 'react';
import { Lock, Eye, EyeOff, Loader2, LogOut, ShieldCheck } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { BCHLogo } from './Layout';

/**
 * Shown when a PIN-encrypted nsec is stored but hasn't been unlocked this
 * session. Blocks the app until the user enters their PIN (or logs out). This
 * is the counterpart to the opt-in encryption in Settings → Security.
 */
export default function UnlockDialog() {
  const { nsecLocked, unlockWithPin, logout } = useAppContext();
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!nsecLocked) return null;

  const handleUnlock = async () => {
    if (!pin) { setError('Enter your PIN.'); return; }
    setError(null);
    setBusy(true);
    try {
      await unlockWithPin(pin);
      setPin('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Incorrect PIN.');
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/95 backdrop-blur-sm p-4">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-2xl">
        <div className="flex flex-col items-center text-center">
          <BCHLogo size={40} />
          <div className="mt-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <h2 className="mt-3 text-lg font-extrabold">Unlock your account</h2>
          <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
            Your key is encrypted on this device. Enter your PIN to continue.
          </p>
        </div>

        <div className="mt-5">
          <div className="relative">
            <input
              type={showPin ? 'text' : 'password'}
              value={pin}
              onChange={e => { setPin(e.target.value); setError(null); }}
              onKeyDown={e => { if (e.key === 'Enter' && !busy) handleUnlock(); }}
              placeholder="PIN or password"
              autoFocus
              inputMode="text"
              autoComplete="off"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
            <button
              type="button"
              onClick={() => setShowPin(s => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground"
              aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
            >
              {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          {error && (
            <p className="mt-2 text-xs font-medium text-red-500">{error}</p>
          )}

          <button
            onClick={handleUnlock}
            disabled={busy || !pin}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-2.5 text-sm font-bold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {busy ? 'Unlocking…' : 'Unlock'}
          </button>

          <button
            onClick={logout}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl py-2 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            <LogOut className="h-3.5 w-3.5" /> Log out instead
          </button>

          <p className="mt-3 text-center text-[10px] leading-relaxed text-muted-foreground/70">
            Forgot your PIN? It can't be recovered — log out and log back in
            with your nsec backup, then set a new PIN.
          </p>
        </div>
      </div>
    </div>
  );
}
